import { computed, inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import type { GuardianChild } from './useGuardianChildSelection';

/**
 * What the parent shell shares with every portal page: the guardian's
 * children, which one is selected, and the state of loading them.
 */
export interface GuardianPortalContext {
  children: Ref<ReadonlyArray<GuardianChild>>;
  /** The switcher's value (writable). */
  selectedChildId: Ref<string>;
  /** The selected child's id once it is confirmed to be one of the children, else ''. */
  childId: Ref<string>;
  selectedChild: Ref<GuardianChild | null>;
  childrenLoading: Ref<boolean>;
  childrenError: Ref<unknown>;
  refetchChildren: () => void;
}

// A string key (not a Symbol) so specs can provide it through mount options.
export const GUARDIAN_PORTAL_CONTEXT = 'guardianPortalContext' as unknown as InjectionKey<GuardianPortalContext>;

export function provideGuardianPortalContext(context: GuardianPortalContext): void {
  provide(GUARDIAN_PORTAL_CONTEXT, context);
}

/** A page rendered outside the shell sees no children rather than crashing. */
function emptyContext(): GuardianPortalContext {
  const selectedChildId = ref('');
  return {
    children: ref([]),
    selectedChildId,
    childId: computed(() => ''),
    selectedChild: computed(() => null),
    childrenLoading: ref(false),
    childrenError: ref(null),
    refetchChildren: () => {},
  };
}

export function useGuardianPortalContext(): GuardianPortalContext {
  return inject(GUARDIAN_PORTAL_CONTEXT, emptyContext, true);
}
